import { createContext, useContext } from 'react';
import {
  CSSObject,
  Grid,
  GridItem,
  GridItemProps,
  HStack,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';

export interface DescriptionsContextProps {
  bordered?: boolean;
  contentProps?: CSSObject;
  labelProps?: CSSObject;
  layout?: 'horizontal' | 'vertical';
  size?: 'middle' | 'small' | 'default';
}
export interface DescriptionsProps extends DescriptionsContextProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  extra?: React.ReactNode;
  column?: number;
  layout?: 'horizontal' | 'vertical';
  headerProps?: CSSObject;
  titleProps?: CSSObject;
  extraProps?: CSSObject;
  gridProps?: CSSObject;
}
export interface DescriptionsItemProps extends GridItemProps {
  label?: React.ReactNode;
  labelProps?: CSSObject;
  contentProps?: CSSObject;
  children: React.ReactNode;
  span?: number;
}
export const DescriptionsContext = createContext<DescriptionsContextProps>({});

const DescriptionsProvider = DescriptionsContext.Provider;
const useDescriptionsContext = (): DescriptionsContextProps =>
  useContext(DescriptionsContext);

export const DescriptionsItem = (props: DescriptionsItemProps) => {
  const { label, labelProps, contentProps, children, span, ...rest } = props;
  const {
    contentProps: contentPropsFromContext,
    labelProps: labelPropsFromContext,
  } = useDescriptionsContext();
  const styles = useMultiStyleConfig('Descriptions', {
    ...props,
    ...useDescriptionsContext(),
  });

  return (
    <GridItem sx={{ ...styles.GridItem }} colSpan={span} {...rest}>
      {label && (
        <Text
          sx={{
            ...styles.Label,
            ...labelPropsFromContext,
            ...labelProps,
          }}
        >
          {label}
        </Text>
      )}
      {children && (
        <Text
          sx={{
            ...styles.Content,
            ...contentPropsFromContext,
            ...contentProps,
          }}
        >
          {children}
        </Text>
      )}
    </GridItem>
  );
};
export const Descriptions = (props: DescriptionsProps) => {
  const {
    bordered = false,
    size = 'default',
    children,
    title,
    extra,
    layout = 'horizontal',
    labelProps,
    contentProps,
    headerProps,
    titleProps,
    extraProps,
    gridProps,
  } = props;
  const styles = useMultiStyleConfig('Descriptions', props);

  return (
    <DescriptionsProvider
      value={{
        bordered: bordered,
        contentProps: contentProps,
        labelProps: labelProps,
        layout: layout,
        size: size,
      }}
    >
      {(title || extra) && (
        <HStack sx={{ ...styles.Header, ...headerProps }}>
          <Text sx={{ ...styles.Title, ...titleProps }}>{title}</Text>
          <Text sx={{ ...styles.Extra, ...extraProps }}>{extra}</Text>
        </HStack>
      )}
      <Grid sx={{ ...styles.Grid, ...gridProps }}>{children}</Grid>
    </DescriptionsProvider>
  );
};

Descriptions.Item = DescriptionsItem;
